// Expansion of Flux shorthand attributes into HTMX attributes.
//
// Precedence (spec §2, §7): raw `hx-*` always wins. If an element already declares the
// target HTMX attribute and it was NOT generated by Flux, Flux leaves it untouched.

import { log } from './logger.js';
import {
  setGeneratedAttribute,
  removeGeneratedAttribute,
  hasGeneratedAttribute,
} from './generated-attributes.js';
import { applyRecipe } from './recipes.js';

// Verbs make an element actionable (matched by HTMX's `[hx-get],[hx-post],...` selector).
const VERBS = ['get', 'post', 'put', 'patch', 'delete'] as const;

// Options configure a request declared by a verb or a preset (e.g. fx-search).
// Note: 'disable' is excluded from generic options so fx-disable remains exclusively for form/submit control disabling.
const OPTIONS = [
  'target',
  'swap',
  'trigger',
  'select',
  'sync',
  'indicator',
  'include',
  'vals',
  'headers',
  'confirm',
  'boost',
  'preload',
  'preserve',
] as const;

// One selector matching any Flux shorthand attribute, used for subtree discovery.
const FLUX_SELECTOR = [
  ...VERBS,
  ...OPTIONS,
  'morph',
  'history',
  'recipe',
  'scope',
  'confirm-dialog',
]
  .map((n) => `[fx-${n}]`)
  .join(',');

/** Returns true if `element` carries any Flux shorthand verb or option attribute. */
export function hasFluxAttributes(element: Element): boolean {
  return element instanceof Element && element.matches(FLUX_SELECTOR);
}

/** CSS selector matching any element carrying a Flux shorthand attribute. */
export function fluxSelector(): string {
  return FLUX_SELECTOR;
}

/**
 * Expands Flux shorthand on `element` into HTMX attributes.
 *
 * Raw `hx-*` wins: an existing HTMX attribute is never overwritten unless owned by Flux.
 * Returns the number of attributes written this call.
 */
export function expandElement(element: Element): number {
  if (!(element instanceof Element)) return 0;

  applyRecipeAndScope(element);

  let written = 0;
  for (const verb of VERBS) written += syncShorthand(element, verb);
  for (const option of OPTIONS) written += syncShorthand(element, option);

  if (element.hasAttribute('fx-confirm-dialog')) {
    if (setGeneratedAttribute(element, 'hx-confirm', 'flux-confirm-dialog')) {
      element.setAttribute('data-flux-gen-confirm-dialog', '1');
      written++;
    }
  } else if (element.hasAttribute('data-flux-gen-confirm-dialog')) {
    removeGeneratedAttribute(element, 'hx-confirm');
    element.removeAttribute('data-flux-gen-confirm-dialog');
    written += syncShorthand(element, 'confirm');
  }

  // fx-prefetch is a Flux preset, not an HTMX shorthand.
  if (element.hasAttribute('data-flux-gen-shorthand-prefetch')) {
    removeGeneratedAttribute(element, 'hx-prefetch');
    element.removeAttribute('data-flux-gen-shorthand-prefetch');
  }

  if (element.hasAttribute('fx-morph')) {
    const rawVal = element.getAttribute('fx-morph')?.trim().toLowerCase() ?? '';
    const validModes: Record<string, string> = {
      '': 'innerMorph',
      inner: 'innerMorph',
      outer: 'outerMorph',
      sync: 'outerSync',
    };
    if (rawVal && !(rawVal in validModes)) {
      log.warn(`invalid fx-morph value "${rawVal}"; falling back to "innerMorph"`);
    }
    const swapMode = validModes[rawVal] ?? 'innerMorph';
    if (setGeneratedAttribute(element, 'hx-swap', swapMode)) {
      element.setAttribute('data-flux-gen-shorthand-morph', '1');
      written++;
    }
  } else if (element.hasAttribute('data-flux-gen-shorthand-morph')) {
    removeGeneratedAttribute(element, 'hx-swap');
    element.removeAttribute('data-flux-gen-shorthand-morph');
  }

  if (element.hasAttribute('fx-history')) {
    const val = element.getAttribute('fx-history') || 'true';
    if (setGeneratedAttribute(element, 'hx-push-url', val)) {
      element.setAttribute('data-flux-gen-shorthand-history', '1');
      written++;
    }
  } else if (element.hasAttribute('data-flux-gen-shorthand-history')) {
    removeGeneratedAttribute(element, 'hx-push-url');
    element.removeAttribute('data-flux-gen-shorthand-history');
  }

  return written;
}

/** Syncs `fx-name` shorthand to `hx-name`. Removes generated `hx-name` if `fx-name` shorthand was removed. */
function syncShorthand(element: Element, name: string): 0 | 1 {
  const fxAttr = `fx-${name}`;
  const hxAttr = `hx-${name}`;
  const marker = `data-flux-gen-shorthand-${name}`;

  const fxValue = element.getAttribute(fxAttr);
  if (fxValue === null) {
    if (element.hasAttribute(marker)) {
      removeGeneratedAttribute(element, hxAttr);
      element.removeAttribute(marker);
    }
    return 0;
  }

  const written = setGeneratedAttribute(element, hxAttr, fxValue);
  if (written || hasGeneratedAttribute(element, hxAttr)) {
    element.setAttribute(marker, '1');
  }
  return written ? 1 : 0;
}

const recipeOwnedAttributes = new WeakMap<Element, Map<string, string>>();
const scopeOwnedAttributes = new WeakMap<Element, Map<string, string>>();
const recipeAndScopeElements = new Set<Element>();

export function applyRecipeAndScope(element: Element): void {
  clearOwnedAttributes(element, recipeOwnedAttributes, 'data-flux-recipe-owned');
  clearOwnedAttributes(element, scopeOwnedAttributes, 'data-flux-scope-owned');
  recipeAndScopeElements.delete(element);

  // Apply Recipe
  if (element.hasAttribute('fx-recipe')) {
    const writtenKeys = new Map<string, string>();
    applyRecipe(element, element.getAttribute('fx-recipe') || '', writtenKeys);
    if (writtenKeys.size > 0) {
      recipeOwnedAttributes.set(element, writtenKeys);
      recipeAndScopeElements.add(element);
      element.setAttribute('data-flux-recipe-owned', '1');
    }
  }

  // Apply Scope(s) Bottom-Up
  const scopes = getScopesBottomUp(element);
  if (scopes.length > 0) {
    const writtenKeys = new Map<string, string>();
    for (const scope of scopes) {
      applyScope(element, scope, writtenKeys);
    }
    if (writtenKeys.size > 0) {
      scopeOwnedAttributes.set(element, writtenKeys);
      recipeAndScopeElements.add(element);
      element.setAttribute('data-flux-scope-owned', '1');
    }
  }
}

export function removeRecipeAndScopeAttributes(root?: Element): void {
  for (const element of Array.from(recipeAndScopeElements)) {
    if (root && element !== root && !root.contains(element)) continue;
    clearOwnedAttributes(element, recipeOwnedAttributes, 'data-flux-recipe-owned');
    clearOwnedAttributes(element, scopeOwnedAttributes, 'data-flux-scope-owned');
    recipeAndScopeElements.delete(element);
  }
}

function clearOwnedAttributes(
  element: Element,
  registry: WeakMap<Element, Map<string, string>>,
  marker: string,
): void {
  const owned = registry.get(element);
  if (owned) {
    for (const [name, value] of owned) {
      if (element.getAttribute(name) === value) element.removeAttribute(name);
    }
    registry.delete(element);
  }
  element.removeAttribute(marker);
}

function getScopesBottomUp(element: Element): Element[] {
  const scopes: Element[] = [];
  let current: Element | null = element.parentElement;
  while (current) {
    if (current.hasAttribute('fx-scope')) {
      scopes.push(current);
    }
    current = current.parentElement;
  }
  return scopes;
}

/** Inherits defaults from the nearest fx-scope if not explicitly set on the element */
function applyScope(element: Element, scope: Element, writtenKeys: Map<string, string>): void {
  for (const attr of Array.from(scope.attributes)) {
    if (attr.name.startsWith('fx-default-')) {
      const originalName = attr.name.replace('fx-default-', 'fx-');
      if (!element.hasAttribute(originalName)) {
        element.setAttribute(originalName, attr.value);
        writtenKeys.set(originalName, attr.value);
      }
    }
  }
}
